'use client';

import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { ProgressCircle } from './progress-1';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';

interface CreditsDropdownProps {
  totalCredits: number;
  remainingCredits: number;
  usedCredits: number;
  className?: string;
}

export function CreditsDropdown({ 
  totalCredits, 
  remainingCredits, 
  usedCredits, 
  className 
}: CreditsDropdownProps) {
  const [selectedOption, setSelectedOption] = React.useState<'total' | 'remaining' | 'used'>('remaining');
  

  const getProgressValue = () => {
    switch (selectedOption) {
      case 'total':
        return 100;
      case 'remaining':
        return totalCredits > 0 ? (remainingCredits / totalCredits) * 100 : 0;
      case 'used':
        return totalCredits > 0 ? (usedCredits / totalCredits) * 100 : 0;
      default:
        return totalCredits > 0 ? (remainingCredits / totalCredits) * 100 : 0;
    }
  };

  const getProgressColor = () => {
    const progressValue = getProgressValue();
    if (progressValue < 60) {
      return 'text-chart-1';
    } else if (progressValue < 90) {
      return 'text-chart-3';
    } else {
      return 'text-destructive';
    }
  };

  const getProgressIndicatorColor = () => {
    const progressValue = getProgressValue();
    if (progressValue < 60) {
      return 'text-chart-1';
    } else if (progressValue < 90) {
      return 'text-chart-3';
    } else {
      return 'text-destructive';
    }
  };

  return (
    <span className={cn("inline-flex items-center gap-x-2.5 rounded-tremor-full bg-background px-2.5 py-1.5 text-tremor-label border", className)}>
      <ProgressCircle
        value={getProgressValue()}
        size={20}
        strokeWidth={2}
        className={getProgressColor()}
        indicatorClassName={getProgressIndicatorColor()}
      />
      
      <span className="text-xs font-medium text-foreground">
        {Math.round(getProgressValue())}%
      </span>
      
      <span className="h-4 w-px bg-border" />
      
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-1 text-xs font-medium hover:bg-accent rounded-sm">
          <span>Credits</span>
          <ChevronDown className="h-3 w-3" />
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          className="w-48 rounded-lg mb-2"
          align="start"
          sideOffset={8}
        >
          <DropdownMenuItem onClick={() => setSelectedOption('total')} className="gap-3 py-2 px-3">
            <div className="flex flex-col">
              <span className="text-xs font-medium">Total Credits</span>
              <span className="text-xs text-muted-foreground">{totalCredits.toLocaleString()}</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setSelectedOption('remaining')} className="gap-3 py-2 px-3">
            <div className="flex flex-col">
              <span className="text-xs font-medium">Remaining Credits</span>
              <span className="text-xs text-muted-foreground">{remainingCredits.toLocaleString()}</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setSelectedOption('used')} className="gap-3 py-2 px-3">
            <div className="flex flex-col">
              <span className="text-xs font-medium">Used Credits</span>
              <span className="text-xs text-muted-foreground">{usedCredits.toLocaleString()}</span>
            </div>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem className="gap-3 py-2 px-3 bg-primary text-primary-foreground rounded-md hover:bg-primary">
            <span className="text-xs font-medium">Upgrade Plan</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
